import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { notamStatus, type Notam, type NotamReason } from '@jupi/shared'
import { utcDayAndTime } from '../weather/weatherFormat'

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' } as const

/** Whether the map has anything to show for it. */
export const notamOnMap = (n: Notam) => n.areas.length > 0 || n.airspaces.length > 0 || n.location !== null

export function validityText(n: Notam, perm: string): string {
  const to = n.to === null ? perm : `${utcDayAndTime(n.to)} UTC${n.toEstimated ? ' EST' : ''}`
  return `${utcDayAndTime(n.from)} UTC – ${to}`
}

export function NotamCard({
  notam,
  now,
  reason,
  distanceNm,
}: {
  notam: Notam
  now: number
  reason?: NotamReason
  distanceNm?: number | null
}) {
  const { t } = useTranslation()
  const status = notamStatus(notam, now)
  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mb: 1 }}>
          <Typography component="span" sx={{ ...mono, fontWeight: 700 }}>
            {notam.id}
          </Typography>
          {notam.location && (
            <Chip size="small" variant="outlined" label={notam.locationName ? `${notam.location} ${notam.locationName}` : notam.location} />
          )}
          <Chip
            size="small"
            color={status === 'active' ? 'warning' : 'default'}
            label={status === 'active' ? t('notam.active') : t('notam.upcoming', { time: utcDayAndTime(notam.from) })}
          />
          {reason && reason !== 'firWide' && (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label={!distanceNm ? t('notam.reason.onRoute') : t('notam.reason.near', { nm: distanceNm })}
            />
          )}
        </Stack>
        <Typography component="pre" sx={{ ...mono, fontSize: '0.85rem', whiteSpace: 'pre-wrap', m: 0 }}>
          {notam.text}
        </Typography>
        <Box sx={{ mt: 1, color: 'text.secondary' }}>
          <Typography variant="body2">{validityText(notam, t('notam.perm'))}</Typography>
          {notam.schedule && <Typography variant="body2">{t('notam.schedule', { schedule: notam.schedule })}</Typography>}
          {(notam.lower || notam.upper) && (
            <Typography variant="body2">{t('notam.limits', { lower: notam.lower ?? '–', upper: notam.upper ?? '–' })}</Typography>
          )}
        </Box>
        {notamOnMap(notam) && (
          <Button
            size="small"
            component={RouterLink}
            to={`/plan/map?notam=${encodeURIComponent(notam.id)}`}
            startIcon={<Icon icon="mdi:map-search-outline" />}
            sx={{ mt: 0.5, ml: -0.5 }}
          >
            {t('notam.showOnMap')}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
